import React, {useEffect, useState} from "react";
import { Link } from "react-router-dom";
import localStyles from "./index.module.css";
import commonStyles from '../../common/commonStyles/table.module.css';
import { Institution } from "../../dashboard";
import {Button, Form, Header, Icon, Loader, Menu, Message, Table} from "semantic-ui-react";
import agent, { PaginateOptions } from "../../../agent";
import { GeneralTitleCase, getGeneralTitle } from "../../../utils/common";
import { hasRole, UserRole } from "../../../helpers/UserRole";
import TablePage from "../../general/TablePage";
import { useUrlPagination } from "../../../hooks/useUrlPagination";
import {
    CommonListPagePaginationResponse,
    DEFAULT_LIMIT,
    DEFAULT_PAGE,
    DEFAULT_SORT_COLUMN,
    handleSortPagination,
    ListState,
} from "../../../helpers/TablePageUtils";
import {textLabels} from "../../../consts/textLabels";

interface InstitutionPaginationResponse extends CommonListPagePaginationResponse<Institution> {}

const initialState: ListState<Institution> = {
    page: DEFAULT_PAGE,
    limit: DEFAULT_LIMIT,
    sortColumn: DEFAULT_SORT_COLUMN,
    searchValue: "",
    docs: [],
    totalDocs: 0,
    totalPages: 1,
    loading: false,
    error: null,
};

const InstitutionsList: React.FC = () => {
    const currentPath = "/dashboard/institutions";

    const { state, setState, updateUrl } = useUrlPagination<ListState<Institution>>(initialState, {
        defaultSortColumn: DEFAULT_SORT_COLUMN,
    });

    const [searchInput, setSearchInput] = useState(state.searchValue);

    const fetchList = async () => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        const paginateOptions: PaginateOptions = {
            page: state.page,
            limit: state.limit,
            sort: state.sortColumn,
            search: state.searchValue,
        };

        try {
            const response: InstitutionPaginationResponse = await agent.InstitutionProvider.getList(paginateOptions);

            if (response) {
                setState(prev => ({
                    ...prev,
                    docs: response.docs,
                    totalDocs: response.totalDocs,
                    totalPages: response.totalPages,
                    loading: false,
                    error: null,
                }));
            }
        } catch (error) {
            console.error(`Ошибка получения списка ${getGeneralTitle(GeneralTitleCase.showInstitutionsButtonBot)}:`, error);
            setState(prev => ({
                ...prev,
                docs: [],
                totalDocs: 0,
                totalPages: 1,
                loading: false,
                error: `Не удалось загрузить список ${getGeneralTitle(GeneralTitleCase.showInstitutionsButtonBot)}. Пожалуйста, попробуйте позже.`,
            }));
        }
    };

    useEffect(() => {
        fetchList();
    }, [state.page, state.limit, state.sortColumn, state.searchValue]);

    useEffect(() => {
        updateUrl();
    }, [state.page, state.limit, state.sortColumn, state.searchValue]);

    const handleSearch = () => {
        setState(prev => ({ ...prev, searchValue: searchInput, page: DEFAULT_PAGE }));
    };

    const handleClearSearch = () => {
        setSearchInput("");
        setState(prev => ({ ...prev, searchValue: "", page: DEFAULT_PAGE }));
    };

    const handleSort = (column: string) => {
        const newSort = handleSortPagination(state.sortColumn, column);
        setState(prev => ({ ...prev, sortColumn: newSort, page: DEFAULT_PAGE }));
    };

    const handlePageChange = (page: number) => {
        setState(prev => ({ ...prev, page }));
    };

    const handleLimitChange = (limit: number) => {
        setState(prev => ({ ...prev, limit, page: DEFAULT_PAGE }));
    };

    const handleSearchInputChange = (value: string) => {
        setSearchInput(value);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const {
        docs,
        totalDocs,
        totalPages,
        loading,
        error,
        page,
        limit,
        sortColumn,
        searchValue,
    } = state;

    const styles = { ...commonStyles, ...localStyles };
    const headerLabel = getGeneralTitle(GeneralTitleCase.showInstitutionsButtonBot);
    const listLoaderLabel = textLabels.listLoaderMessage;
    const errorLabel = textLabels.errorMessage;

    return (
        <div className={styles.table}>
            <div className={styles.header}><Header>{headerLabel}</Header></div>

            {error && (
                <Message negative>
                    <Message.Header>{errorLabel}</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            {loading ? (
                <Loader active>{listLoaderLabel}</Loader>
            ) : (
                <div className={styles.list}>
                    <Form>
                        <Form.Group>
                            <Form.Input
                                placeholder="Наименование"
                                iconPosition="left"
                                icon={<Icon name="search" />}
                                value={searchInput}
                                onChange={(e) => handleSearchInputChange(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                            <Form.Button primary content="Поиск" onClick={handleSearch} />
                            <Form.Button basic color="orange" content="Сбросить" onClick={handleClearSearch} />
                        </Form.Group>
                    </Form>

                    <div className={styles.container}>
                        <Table celled selectable striped verticalAlign="middle" color="teal">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell
                                        sorted={
                                            sortColumn === "name" || sortColumn === "-name"
                                                ? sortColumn.startsWith("-")
                                                    ? "descending"
                                                    : "ascending"
                                                : undefined
                                        }
                                        onClick={() => handleSort("name")}
                                    >
                                        Наименование
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>Ссылка</Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={
                                            sortColumn === "active" || sortColumn === "-active"
                                                ? sortColumn.startsWith("-")
                                                    ? "descending"
                                                    : "ascending"
                                                : undefined
                                        }
                                        onClick={() => handleSort("active")}
                                    >
                                        Активность в боте
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {docs.map((el, index) => (
                                    <Table.Row style={{ cursor: "pointer" }} key={`key-${index}-${el._id}`}>
                                        <Table.Cell floated="left">
                                            {hasRole(UserRole.ADMIN) ? (
                                                <Link
                                                    className={styles.nameColumn}
                                                    to={{
                                                        pathname: `${currentPath}/${el._id}/edit`,
                                                        search: `?returnUrl=${encodeURIComponent(window.location.href)}`,
                                                    }}
                                                >
                                                    {el.name}
                                                </Link>
                                            ) : (
                                                el.name
                                            )}
                                        </Table.Cell>
                                        <Table.Cell>{el.link}</Table.Cell>
                                        <Table.Cell>
                                            <Form.Checkbox readOnly checked={el.active === true} />
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>

                            <Table.Footer>
                                <Table.Row>
                                    <Table.HeaderCell colSpan="3">
                                        <Menu floated="right" pagination>
                                            <TablePage
                                                currentPage={page}
                                                totalPages={totalPages}
                                                totalDocs={totalDocs}
                                                limit={limit}
                                                onPageChange={handlePageChange}
                                                onLimitChange={handleLimitChange}
                                            />
                                        </Menu>
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Footer>
                        </Table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InstitutionsList;